"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DownloadManagerService = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const modal_service_1 = require("../../components/modal/modal.service");
const i18n_1 = require("../../i18n");
const services_1 = require("../../services");
const store_1 = require("../../store");
const export_notification_ctrl_1 = require("../export.notification/export.notification.ctrl");
const export_notification_1 = require("../export.notification/export.notification");
const export_notification_style_1 = require("../export.notification/export.notification.style");
const notification_service_1 = require("../../components/notification.toast/notification.service");
const POLL_INTERVAL_MS = 5000;
/** Statuses that indicate the task has reached a terminal state */
function isTerminalStatus(status) {
    return status !== core_api_1.LongRunningTaskStatus.RUNNING;
}
class DownloadManagerService {
    constructor(state) {
        this.completionResolvers = new Map();
        this.stopNavigate = () => {
            return this.state.pendingExports > 0 || this.state.hasActiveTasks;
        };
        this.state = state;
        this.setupBeforeUnloadWarning();
    }
    /**
     * Returns a promise that resolves when the given task reaches a terminal state.
     * If the task is already terminal, resolves immediately.
     * Used by the legacy export mode to block until the export completes.
     */
    awaitTaskCompletion(taskId) {
        const task = this.state.tasks.find((t) => t.taskId === taskId);
        if (task != null && isTerminalStatus(task.status)) {
            return Promise.resolve(task);
        }
        return new Promise((resolve) => {
            this.completionResolvers.set(taskId, resolve);
        });
    }
    /**
     * Start a new long-running export task.
     * Returns immediately after the task is created on the backend.
     * Polling handles the rest.
     *
     * Units are resolved server-side from the user's DB settings.
     */
    startExportTask(exportType, revisions, exportTypeLabel, jobName, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a;
            const departmentId = (_a = services_1.Services.Department.current) === null || _a === void 0 ? void 0 : _a.objectId;
            if (!departmentId)
                return '';
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = yield services_1.Services.Department.rest.Task.create({
                type: 'export',
                payload: {
                    exportType,
                    revisions,
                    timezone,
                    noPhotos: options === null || options === void 0 ? void 0 : options.noPhotos,
                },
            });
            // Add to state immediately so the UI reflects the new task
            this.state.addTask({
                taskId: response.taskId,
                exportType: exportTypeLabel,
                jobName,
                status: response.status,
                startedAt: Date.now(),
                departmentId,
                silent: options === null || options === void 0 ? void 0 : options.silent,
            });
            if (!this.state.isPopoverOpen)
                this.state.isPulsing = true;
            if (!(options === null || options === void 0 ? void 0 : options.silent))
                this.notifyExportStarted(exportTypeLabel, jobName);
            // Cache hit — handle the terminal response directly
            if (isTerminalStatus(response.status)) {
                yield this.handleTaskCompleted(response.taskId, response.status, response.result, response.notifications);
                return response.taskId;
            }
            // Still running — poll for completion
            this.startPolling(response.taskId);
            return response.taskId;
        });
    }
    /**
     * Register a fast export (KML, JSON) in the download manager.
     * Called before initiating the fast export HTTP request.
     * Returns a local taskId so the caller can update the task on completion/failure.
     */
    addFastExportTask(exportType, jobName, options) {
        var _a;
        const departmentId = (_a = services_1.Services.Department.current) === null || _a === void 0 ? void 0 : _a.objectId;
        if (!departmentId)
            return '';
        const taskId = `fast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        this.state.addTask({
            taskId,
            exportType,
            jobName,
            status: core_api_1.LongRunningTaskStatus.RUNNING,
            startedAt: Date.now(),
            departmentId,
            silent: options === null || options === void 0 ? void 0 : options.silent,
        });
        if (!this.state.isPopoverOpen)
            this.state.isPulsing = true;
        if (!(options === null || options === void 0 ? void 0 : options.silent))
            this.notifyExportStarted(exportType, jobName);
        return taskId;
    }
    /** Mark a fast export task as completed with a download URL */
    completeFastExportTask(taskId, url, fileName) {
        this.state.updateTask(taskId, {
            status: core_api_1.LongRunningTaskStatus.COMPLETED,
            result: { exportId: '', fileName, url },
            blobUrl: url,
            seen: false,
            notified: this.state.isPopoverOpen,
        });
        if (!this.state.isPopoverOpen)
            this.state.isPulsing = true;
        const task = this.state.tasks.find((t) => t.taskId === taskId);
        if (task && !task.silent)
            this.notifyExportFinished(task.exportType, core_api_1.LongRunningTaskStatus.COMPLETED);
        this.resolveCompletion(taskId);
    }
    /** Mark a fast export task as failed or completed-with-notifications */
    failFastExportTask(taskId, notifications, error) {
        let status;
        if (notifications != null && notifications.length > 0) {
            const hasErrors = notifications.some((n) => Array.isArray(n.errors) && n.errors.length > 0);
            status = hasErrors ? core_api_1.LongRunningTaskStatus.COMPLETED_ERRORS : core_api_1.LongRunningTaskStatus.COMPLETED_WARNINGS;
            this.state.updateTask(taskId, { status, notifications, seen: false, notified: this.state.isPopoverOpen });
        }
        else {
            status = core_api_1.LongRunningTaskStatus.FAILED;
            this.state.updateTask(taskId, {
                status,
                error: error !== null && error !== void 0 ? error : (0, i18n_1.Translate)(i18n_1.T.ERROR_GENERAL),
                seen: false,
                notified: this.state.isPopoverOpen,
            });
        }
        if (!this.state.isPopoverOpen)
            this.state.isPulsing = true;
        const task = this.state.tasks.find((t) => t.taskId === taskId);
        if (task && !task.silent)
            this.notifyExportFinished(task.exportType, status);
        this.resolveCompletion(taskId);
    }
    /** Manually trigger download for a completed task */
    downloadTask(taskId) {
        var _a;
        const task = this.state.tasks.find((t) => t.taskId === taskId);
        if (task === null || task === void 0 ? void 0 : task.result) {
            services_1.Services.Download.download((_a = task.blobUrl) !== null && _a !== void 0 ? _a : task.result.url, task.result.fileName);
            this.state.updateTask(taskId, { downloaded: true });
        }
    }
    /**
     * Open the export notification modal for a task with warnings or errors.
     * For warnings: user can confirm to proceed with download.
     * For errors: informational only, no download available.
     */
    showNotificationModal(taskId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const task = this.state.tasks.find((t) => t.taskId === taskId);
            if (task == null)
                return;
            const notifications = (_a = task.notifications) !== null && _a !== void 0 ? _a : [];
            if (notifications.length === 0)
                return;
            const ctrl = new export_notification_ctrl_1.ExportNotificationCtrl(notifications);
            const revisions = ctrl.getRevisions();
            // Fetch collections before opening the modal so content renders immediately
            const collections = (yield Promise.all(revisions.map((revision) => store_1.AppStore.Collection.fetch(revision)))).filter((c) => c != null);
            ctrl.setCollections(collections);
            const result = yield modal_service_1.ModalService.open({
                title: export_notification_1.ExportNotificationTitle.view(ctrl),
                content: export_notification_1.ExportNotificationContent.view(ctrl),
                hasCancel: !ctrl.errors,
                className: export_notification_style_1.CSS.modal,
            });
            // For warnings: if user clicks OK, proceed with download
            if (!ctrl.errors && result.ok && ((_b = task.result) === null || _b === void 0 ? void 0 : _b.url)) {
                services_1.Services.Download.download((_c = task.blobUrl) !== null && _c !== void 0 ? _c : task.result.url, task.result.fileName);
            }
        });
    }
    /**
     * Shared handler for terminal task responses.
     * Handles COMPLETED, COMPLETED_WARNINGS, COMPLETED_ERRORS, and FAILED.
     */
    handleTaskCompleted(taskId, status, result, notifications, error) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // Prefetch the blob before updating state so the row transitions
            // to its final display status at the same time as the toast fires
            let blobUrl;
            if (result === null || result === void 0 ? void 0 : result.url) {
                blobUrl = yield this.prefetchBlob(result.url);
            }
            this.state.updateTask(taskId, {
                status,
                result,
                notifications,
                error,
                seen: false,
                notified: this.state.isPopoverOpen,
                blobUrl,
            });
            if (!this.state.isPopoverOpen)
                this.state.isPulsing = true;
            const task = this.state.tasks.find((t) => t.taskId === taskId);
            if (task && !task.silent)
                this.notifyExportFinished(task.exportType, status);
            this.resolveCompletion(taskId);
        });
    }
    startPolling(taskId) {
        const poll = () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield services_1.Services.Department.rest.Task.status({ taskId });
                if (isTerminalStatus(response.status)) {
                    yield this.handleTaskCompleted(taskId, response.status, response.result, response.notifications, response.error);
                    return;
                }
                this.state.updateTask(taskId, { status: response.status });
            }
            catch (_err) {
                // Transient error — will retry on the next interval
            }
            // Schedule next poll
            const handle = setTimeout(poll, POLL_INTERVAL_MS);
            this.state.updateTask(taskId, { pollHandle: handle });
        });
        const handle = setTimeout(poll, POLL_INTERVAL_MS);
        this.state.updateTask(taskId, { pollHandle: handle });
    }
    /** Resolve any pending awaitTaskCompletion promise for the given taskId */
    resolveCompletion(taskId) {
        const resolver = this.completionResolvers.get(taskId);
        if (resolver) {
            const task = this.state.tasks.find((t) => t.taskId === taskId);
            if (task)
                resolver(task);
            this.completionResolvers.delete(taskId);
        }
    }
    prefetchBlob(url) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch(url);
                const blob = yield response.blob();
                return URL.createObjectURL(blob);
            }
            catch (_a) {
                return url;
            }
        });
    }
    notifyExportStarted(exportType, jobName) {
        notification_service_1.NotificationService.success({ key: i18n_1.T.EXPORT_STARTED, attrs: { type: exportType, name: jobName } });
    }
    notifyExportFinished(exportType, status) {
        const attrs = { type: exportType };
        if (status === core_api_1.LongRunningTaskStatus.COMPLETED) {
            notification_service_1.NotificationService.success({ key: i18n_1.T.EXPORT_FINISHED, attrs });
        }
        else if (status === core_api_1.LongRunningTaskStatus.COMPLETED_WARNINGS) {
            notification_service_1.NotificationService.warn({
                key: i18n_1.T.EXPORT_FINISHED,
                attrs: Object.assign(Object.assign({}, attrs), { issue: (0, i18n_1.Translate)(i18n_1.T.EXPORT_FINISHED_WARNINGS) }),
            });
        }
        else {
            notification_service_1.NotificationService.error({
                key: i18n_1.T.EXPORT_FINISHED,
                attrs: Object.assign(Object.assign({}, attrs), { issue: (0, i18n_1.Translate)(i18n_1.T.EXPORT_FINISHED_ERRORS) }),
            });
        }
    }
    setupBeforeUnloadWarning() {
        if (services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_NEW_EXPORT_UI)) {
            // New UI: exports run in the background so only warn on tab close, not internal navigation
            services_1.Services.Window.window.addEventListener('beforeunload', (event) => {
                if (this.stopNavigate()) {
                    event.preventDefault();
                    event.returnValue = (0, i18n_1.Translate)(i18n_1.T.EXPORT_MANAGER_NAVIGATE_AWAY);
                }
            });
        }
        else {
            // Legacy UI: block both tab close and internal navigation while exports are active
            services_1.Services.Window.bindNavigateAway(i18n_1.T.EXPORT_MANAGER_NAVIGATE_AWAY, this.stopNavigate, {
                modalTitle: i18n_1.T.EXPORT_MANAGER_NAVIGATE_TITLE,
                modalContent: i18n_1.T.EXPORT_MANAGER_NAVIGATE_AWAY,
                blockNavigation: true,
            });
        }
    }
}
exports.DownloadManagerService = DownloadManagerService;
//# sourceMappingURL=download.manager.service.js.map